import { applySceneRecipes } from "./sceneRecipes";

const POLOTNO_ANIMATION_TYPES = new Set(["enter", "exit", "loop"]);
const POLOTNO_ANIMATION_NAMES = new Set([
  "fade",
  "move",
  "zoom",
  "rotate",
  "blink",
  "bounce",
  "cameraZoom",
]);

function normalizeRole(value = "") {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeAnimations(animations = []) {
  if (!Array.isArray(animations)) {
    return [];
  }

  return animations
    .map((animation) => {
      const type = POLOTNO_ANIMATION_TYPES.has(animation?.type) ? animation.type : "enter";
      const name = POLOTNO_ANIMATION_NAMES.has(animation?.name) ? animation.name : "fade";
      return {
        type,
        name,
        delay: Math.max(Number(animation?.delay) || 0, 0),
        duration: Math.max(Number(animation?.duration) || 700, 1),
        enabled: animation?.enabled !== false,
        data: animation?.data && typeof animation.data === "object" ? animation.data : {},
      };
    })
    .filter((animation) => animation.duration > 0);
}

function buildBaseNode({
  id,
  type,
  name,
  x,
  y,
  width,
  height,
  opacity = 1,
  rotation = 0,
  animations = [],
}) {
  return {
    id,
    type,
    name,
    opacity,
    custom: {},
    visible: true,
    selectable: true,
    removable: true,
    alwaysOnTop: false,
    showInExport: true,
    x,
    y,
    width,
    height,
    rotation,
    animations: normalizeAnimations(animations),
    blurEnabled: false,
    blurRadius: 10,
    brightnessEnabled: false,
    brightness: 0,
    sepiaEnabled: false,
    grayscaleEnabled: false,
    filters: {},
    shadowEnabled: false,
    shadowBlur: 5,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowColor: "black",
    shadowOpacity: 1,
    draggable: true,
    resizable: true,
    contentEditable: true,
    styleEditable: true,
  };
}

function selectedAssetMatchesSceneSlot(asset = {}, element = {}) {
  if (asset.binding_scope !== "scene_slot") return false;
  const sceneId = element.scene_id || element.page_id || "";
  const elementId = element.id || "";
  const role = normalizeRole(element.asset_binding?.asset_role || element.asset_binding?.asset_key || element.role);
  return (
    (asset.slot_key && asset.slot_key === element.slot_key) ||
    (asset.scene_id && asset.element_id && asset.scene_id === sceneId && asset.element_id === elementId) ||
    (asset.scene_id === sceneId && normalizeRole(asset.role || asset.asset_role || asset.asset_key) === role)
  );
}

function resolveAssetUrl(binding = {}, selectedAssets = [], element = {}) {
  const role = binding.asset_role || binding.asset_key;
  const normalizedRole = normalizeRole(role);
  const sceneSlotMatch = selectedAssets.find((asset) => selectedAssetMatchesSceneSlot(asset, element));
  if (sceneSlotMatch) {
    return (
      sceneSlotMatch.media_url ||
      sceneSlotMatch.file_url ||
      sceneSlotMatch.thumbnail_url ||
      sceneSlotMatch.preview_url ||
      binding.media_url ||
      ""
    );
  }

  const matched = selectedAssets.find(
    (asset) =>
      asset.binding_scope !== "scene_slot" &&
      (normalizeRole(asset.role) === normalizedRole ||
        normalizeRole(asset.asset_role) === normalizedRole ||
        normalizeRole(asset.asset_key) === normalizedRole ||
        String(asset.asset_library_item_id || "") === String(binding.asset_library_item_id || ""))
  );

  return matched?.media_url || matched?.file_url || matched?.thumbnail_url || matched?.preview_url || binding.media_url || "";
}

function isSvgUrl(url = "", binding = {}) {
  return (
    String(url).toLowerCase().includes(".svg") ||
    String(binding.asset_type || "").toLowerCase().includes("svg")
  );
}

function buildTextNode(element) {
  const placement = element.placement || {};
  const text = element.text || {};

  return {
    ...buildBaseNode({
      id: element.id,
      type: "text",
      name: `slot:${element.role || "text"} text:${text.placeholder_key || element.role || element.id}`,
      x: placement.x || 0,
      y: placement.y || 0,
      width: placement.width || 400,
      height: placement.height || 120,
      opacity: element.opacity ?? 1,
      rotation: element.rotation || 0,
      animations: element.animation || [],
    }),
    text: text.content || "",
    placeholder: "",
    fontFamily: text.font_family || "Cormorant Garamond",
    fontSize: text.font_size || 36,
    fontWeight: text.font_weight || "normal",
    fontStyle: text.font_style || "normal",
    textDecoration: "",
    fill: text.fill || "#ffffff",
    align: text.align || "center",
    verticalAlign: "top",
    strokeWidth: 0,
    stroke: "black",
    lineHeight: text.line_height || 1.2,
    letterSpacing: text.letter_spacing || 0,
    backgroundEnabled: false,
    backgroundColor: "#7ED321",
    backgroundOpacity: 1,
    backgroundCornerRadius: 0.5,
    backgroundPadding: 0.5,
    custom: {
      role: element.role || "",
    },
  };
}

function buildFigureNode(element) {
  const placement = element.placement || {};
  const figure = element.figure || {};

  return {
    ...buildBaseNode({
      id: element.id,
      type: "figure",
      name: `shape:${element.role || element.id}`,
      x: placement.x || 0,
      y: placement.y || 0,
      width: placement.width || 400,
      height: placement.height || 400,
      opacity: element.opacity ?? 1,
      rotation: element.rotation || 0,
      animations: element.animation || [],
    }),
    subType: figure.sub_type || "rect",
    fill: figure.fill || "rgba(255,255,255,0.25)",
    dash: [],
    stroke: figure.stroke || "rgba(0,0,0,0)",
    strokeWidth: figure.stroke_width || 0,
    cornerRadius: figure.corner_radius || 0,
    custom: {
      role: element.role || "",
    },
  };
}

function buildAssetNode(element, selectedAssets = []) {
  const placement = element.placement || {};
  const binding = element.asset_binding || {};
  const src = resolveAssetUrl(binding, selectedAssets, element);
  if (!src) {
    return buildFigureNode({
      ...element,
      type: "figure",
      role: `missing_${binding.asset_role || element.role || "asset"}`,
      figure: {
        sub_type: "rect",
        fill: "rgba(255, 243, 209, 0.28)",
        stroke: "rgba(212, 175, 55, 0.58)",
        stroke_width: 2,
        corner_radius: 22,
      },
    });
  }

  const type = isSvgUrl(src, binding) ? "svg" : "image";
  const base = buildBaseNode({
    id: element.id,
    type,
    name: `asset:${binding.asset_role || element.role || element.id}`,
    x: placement.x || 0,
    y: placement.y || 0,
    width: placement.width || 400,
    height: placement.height || 400,
    opacity: element.opacity ?? 1,
    rotation: element.rotation || 0,
    animations: element.animation || [],
  });

  if (type === "svg") {
    return {
      ...base,
      src,
      maskSrc: "",
      cropX: 0,
      cropY: 0,
      cropWidth: 1,
      cropHeight: 1,
      keepRatio: true,
      stretchEnabled: false,
      flipX: false,
      flipY: false,
      borderColor: "rgba(0,0,0,0)",
      borderSize: 0,
      cornerRadius: 0,
      custom: {
        role: element.role || "",
        asset_role: binding.asset_role || binding.asset_key || "",
      },
    };
  }

  return {
    ...base,
    src,
    cropX: 0,
    cropY: 0,
    cropWidth: 1,
    cropHeight: 1,
    keepRatio: placement.fit === "contain",
    flipX: false,
    flipY: false,
    clipSrc: "",
    borderColor: "rgba(0,0,0,0)",
    borderSize: 0,
    cornerRadius: 0,
    custom: {
      role: element.role || "",
      asset_role: binding.asset_role || binding.asset_key || "",
    },
  };
}

function buildElementNode(element, selectedAssets = []) {
  if (element.type === "text") return buildTextNode(element);
  if (element.type === "figure") return buildFigureNode(element);
  if (["image", "svg", "video"].includes(element.type)) {
    return buildAssetNode(element, selectedAssets);
  }

  return null;
}

export function compositionPlanToPolotnoDraft({ compositionPlan, selectedAssets = [], family = {} }) {
  const enrichedCompositionPlan = applySceneRecipes(compositionPlan, selectedAssets, family);
  const canvas = enrichedCompositionPlan?.canvas || {};
  const width = canvas.width || 1080;
  const height = canvas.height || 1920;

  return {
    width,
    height,
    fonts: [],
    audios: [],
    custom: {
      generation_target: "admin_ai_template_v3",
      template_family: compositionPlan?.template_family || "",
    },
    schemaVersion: 2,
    unit: "px",
    dpi: 72,
    pages: (enrichedCompositionPlan?.scenes || []).map((scene, sceneIndex) => {
      const sortedElements = [...(scene.elements || [])].sort(
        (left, right) => (left.layer_index || 0) - (right.layer_index || 0)
      );
      const children = sortedElements
        .map((element) =>
          buildElementNode(
            {
              ...element,
              scene_id: scene.scene_id || scene.page_id || `scene-${sceneIndex + 1}`,
              page_id: scene.page_id || scene.scene_id || `scene-${sceneIndex + 1}`,
              slot_key: `${scene.scene_id || scene.page_id || `scene-${sceneIndex + 1}`}:${
                normalizeRole(element.asset_binding?.asset_role || element.asset_binding?.asset_key || element.role)
              }:${element.id || 0}`,
            },
            selectedAssets
          )
        )
        .filter(Boolean);

      return {
        id: scene.page_id || scene.scene_id || `scene-${sceneIndex + 1}`,
        name: scene.name || `Scene ${sceneIndex + 1}`,
        width,
        height,
        background: scene.background?.color || "rgba(74, 18, 8, 1)",
        bleed: 0,
        custom: {
          scene_id: scene.scene_id || scene.page_id || `scene-${sceneIndex + 1}`,
          scene_name: scene.name || `Scene ${sceneIndex + 1}`,
        },
        duration: scene.duration_ms || 5000,
        children,
      };
    }),
  };
}
